import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cachedPriorAuthService, cachedPatientService } from '../services/cachedApiService';
import { PriorAuthorization, Patient } from '../types';
import Layout from '../components/Layout';
import '../styles/PatientList.css';
import '../styles/PriorAuthorizationList.css';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';
import ReactDOM from 'react-dom';

const PriorAuthorizationList: React.FC = () => {
  const { patientId } = useParams<{ patientId?: string }>();
  const [authorizations, setAuthorizations] = useState<PriorAuthorization[]>([]);
  const [filteredAuthorizations, setFilteredAuthorizations] = useState<PriorAuthorization[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [patient, setPatient] = useState<Patient | null>(null);
  const [patients, setPatients] = useState<Record<string, Patient>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();
  const [selectedLetterId, setSelectedLetterId] = useState<number | null>(null);
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let authData: PriorAuthorization[] = [];

        if (patientId) {
          authData = await cachedPriorAuthService.getPatientPriorAuthorizations(patientId);
          const patientDetail = await cachedPatientService.getPatientDetail(patientId);
          setPatient(patientDetail.patient);
        } else {
          authData = await cachedPriorAuthService.getAllPriorAuthorizations();
          
          // Fetch all patients to display their names
          const allPatients = await cachedPatientService.getAllPatients();
          const patientMap: Record<string, Patient> = {};
          allPatients.forEach(patient => {
            patientMap[patient.pid] = patient;
          });
          setPatients(patientMap);
        }

        setAuthorizations(authData);
        setFilteredAuthorizations(authData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch prior authorizations');
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this prior authorization?')) {
      try {
        await cachedPriorAuthService.deletePriorAuth(id);
        // The cache is automatically invalidated in the cachedPriorAuthService
        const updatedAuthorizations = authorizations.filter(auth => auth.id !== id);
        setAuthorizations(updatedAuthorizations);
        setFilteredAuthorizations(updatedAuthorizations.filter(auth =>
          auth.id.toString().includes(searchTerm) ||
          auth.request_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          auth.urgency.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(searchTerm.toLowerCase()))
        ));
      } catch (err) {
        setError('Failed to delete prior authorization');
      }
    }
  };
   const Modal = ({ authId, onClose }: { authId: number; onClose: () => void }) => {
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState<string | null>(null);
      const [pdfData, setPdfData] = useState<string | null>(null);
  
      useEffect(() => {
        const fetchPdfData = async () => {
          try {
            setLoading(true);
            // Use absolute URL path to avoid relative path issues
            const response = await fetch(
              `/api/pa-letter-data/${authId}/`,
              { credentials: 'include', headers: { Accept: 'application/json' } }
            );
            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to fetch PDF data');
            }
            const data = await response.json();
            setPdfData(data.data);
            setLoading(false);
          } catch (err: any) {
            setError(err.message || 'Failed to load the letter. Please try again.');
            setLoading(false);
          }
        };
        fetchPdfData();
      }, [authId]);
  
      return ReactDOM.createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 style={{ fontSize: '1.2rem', margin: '0' }}>Prior Authorization Letter</h3>
              <button className="modal-close" onClick={onClose} aria-label="Close modal" style={{ padding: '5px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            </div>
            <div className="pdf-container">
              {loading && (
                <div className="loading-container" style={{ padding: '30px 0' }}>
                  <div className="loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '15px' }}></div>
                  <div className="loading-text" style={{ fontSize: '0.9rem' }}>Loading letter...</div>
                </div>
              )}
              {error && <div className="error">{error}</div>}
              {pdfData && !loading && !error && (
                <iframe
                  src={`data:application/pdf;base64,${pdfData}`}
                  title="PA Letter"
                  width="100%"
                  height="450px"
                  style={{ border: '1px solid #edf2f7', borderRadius: '6px' }}
                />
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => window.print()} style={{ padding: '6px 12px', fontSize: '12px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                  <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" />
                  <path d="M6 18H4C3.47 18 2.96 17.79 2.59 17.41C2.21 17.04 2 16.53 2 16V11C2 10.47 2.21 9.96 2.59 9.59C2.96 9.21 3.47 9 4 9H20C20.53 9 21.04 9.21 21.41 9.59C21.79 9.96 22 10.47 22 11V16C22 16.53 21.79 17.04 21.41 17.41C21.04 17.79 20.53 18 20 18H18" stroke="currentColor" strokeWidth="2" />
                  <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" />
                </svg>
                Print
              </button>
              <button className="btn btn-cancel" onClick={onClose} style={{ padding: '6px 12px', fontSize: '12px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
    };
  if (loading) {
    return (
      <Layout>
        <div className="loading" style={{ padding: '40px', fontSize: '1rem' }}>
          <div className="loading-spinner" style={{ width: '50px', height: '50px', marginBottom: '20px' }}></div>
          Loading prior authorizations...
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error">
          {error}
          <button
            onClick={() => {
              invalidate.priorAuths();
              if (patientId) {
                invalidate.patients(patientId);
              }
              window.location.reload();
            }}
            className="btn btn-primary mt-3"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  const pageTitle = patientId 
    ? `Prior Authorizations for ${patient?.fname} ${patient?.lname}`
    : 'All Prior Authorizations';

  return (
    <Layout>
      <div className="container">
        <h2 className="mb-3 d-flex align-items-center justify-content-center gap-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ background: 'linear-gradient(45deg, #3182ce, #4299e1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: '1.75rem' }}>
            {pageTitle}
          </span>
        </h2>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by ID, type, urgency, or reason..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.trim() === '') {
                  setFilteredAuthorizations(authorizations);
                } else {
                  setFilteredAuthorizations(
                    authorizations.filter(auth =>
                      auth.id.toString().includes(value) ||
                      auth.request_type.toLowerCase().includes(value.toLowerCase()) ||
                      auth.urgency.toLowerCase().includes(value.toLowerCase()) ||
                      (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(value.toLowerCase()))
                    )
                  );
                }
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredAuthorizations(authorizations);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="search-results-count">
            {filteredAuthorizations.length} {filteredAuthorizations.length === 1 ? 'authorization' : 'authorizations'} found
          </div>
        </div>

        {filteredAuthorizations.length > 0 ? (
          <ul className="p-0" style={{ listStyle: 'none' }}>
            {filteredAuthorizations.map((auth) => (
              <li key={auth.id} className="patient-card fade-in">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar">
                    {auth.request_type === 'prescription' ? 'Rx' : 'Pr'}
                  </div>
                  <div className="patient-name">
                    <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#2d3748' }}>
                      {auth.request_type.charAt(0).toUpperCase() + auth.request_type.slice(1)}
                    </span>
                    <div style={{ fontSize: '11px', color: '#718096', marginTop: '2px' }}>
                      {patientId ? (
                        `Created: ${auth.created_at?.split(' ')[0].slice(0,10)}`
                      ) : (
                        <>
                          <span style={{ fontWeight: '500', color: '#4a5568' }}>
                            {patients[auth.patient] ?
                              `${patients[auth.patient].fname} ${patients[auth.patient].lname}` :
                              auth.patient}
                          </span>
                          <span style={{ margin: '0 4px' }}>•</span>
                          <span>{auth.created_at?.split(' ')[0].slice(0,10)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {/* Status badge */}
                  <span className={`badge ${
                    auth.status === 'approved'
                      ? 'bg-success'
                      : auth.status === 'rejected'
                        ? 'bg-danger'
                        : auth.status === 'pending'
                          ? 'bg-warning'
                          : 'bg-secondary'
                  }`} style={{ padding: '4px 10px', fontSize: '11px' }}>
                    {auth.status === 'approved' ? (
                      <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                        <path d="M5 10L9 14L20 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : auth.status === 'rejected' ? (
                      <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                        <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                        <circle cx="6" cy="6" r="5" stroke="white" strokeWidth="2" />
                        <path d="M6 8V6M6 4H6.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {auth.status ? auth.status.charAt(0).toUpperCase() + auth.status.slice(1) : 'Pending'}
                  </span>

                  {/* Urgency badge */}
                  <span className={`badge ${auth.urgency === 'urgent' ? 'warning' : 'normal'}`} style={{ padding: '4px 10px', fontSize: '11px' }}>
                    {auth.urgency === 'urgent' ? (
                      <svg width="12" height="12" fill="none" style={{ marginRight: '4px' }}>
                        <path d="M12 9V13M12 17H12.01M5.1 19H18.9C20.5 19 21.4 17.33 20.7 16L13.7 4C13 2.67 11 2.67 10.3 4L3.3 16C2.6 17.33 3.5 19 5.1 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" fill="none" style={{ marginRight: '4px' }}>
                        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="2" />
                        <path d="M6 8V6M6 4H6.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {auth.urgency.charAt(0).toUpperCase() + auth.urgency.slice(1)}
                  </span>
                  
                  {/* Clinical reason */}
                  <span className="status-badge" style={{ background: 'linear-gradient(45deg, #ebf4ff, #e6fffa)', color: '#2c5282', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderLeft: '3px solid #4299e1', padding: '4px 10px', fontSize: '11px' }}>
                    {auth.clinical_reason || 'No clinical reason provided'}
                  </span>
                  
                  {/* Rejection reason if applicable */}
                  {auth.status === 'rejected' && auth.rejection_reason && (
                    <span className="status-badge" style={{ background: '#fff5f5', color: '#c53030', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderLeft: '3px solid #e53e3e', padding: '4px 10px', fontSize: '11px' }}>
                      Reason: {auth.rejection_reason}
                    </span>
                  )}

                  {/* Action buttons */}
                  {auth.letter_file ? (
                    <button
                      onClick={() => setSelectedLetterId(auth.id)}
                      className="btn btn-view-letter btn-sm"
                      title="View Authorization Letter"
                    >
                      <svg width="14" height="14" fill="none" style={{ marginRight: '4px' }}>
                        <path d="M1 7S3 3 7 3s6 4 6 4-2 4-6 4S1 7 1 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Letter
                    </button>
                  ) : (
                    <span className="no-letter-badge">
                      <svg width="12" height="12" fill="none" style={{ marginRight: '3px' }}>
                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      No Letter
                    </span>
                  )}
                  
                  {(user?.role === 'superuser' || user?.role === 'provider') && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(auth.id)}
                      title="Delete"
                    >
                      <svg width="14" height="14" fill="none" style={{ marginRight: '3px' }}>
                        <path d="M10 4L9.5 10.5C9.5 11 9 11.5 8.5 11.5H4.5C4 11.5 3.5 11 3.5 10.5L3 4M5.5 6V9M7.5 6V9M8 4V2.5C8 2.25 7.75 2 7.5 2H5.5C5.25 2 5 2.25 5 2.5V4M2.5 4H10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Del
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty">
            <svg width="48" height="48" fill="none" style={{ marginBottom: '15px', opacity: 0.6 }}>
              <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div style={{ fontSize: '16px', fontWeight: '500', color: '#718096' }}>
              {authorizations.length > 0
                ? `No authorizations found matching "${searchTerm}"`
                : "No prior authorizations found"}
            </div>
            <p style={{ fontSize: '13px', color: '#a0aec0', marginTop: '8px' }}>
              {authorizations.length > 0
                ? "Try a different search term or clear the search"
                : "Create a new authorization request to get started"}
            </p>
            {authorizations.length > 0 && searchTerm && (
              <button
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '8px', padding: '6px 12px', fontSize: '12px' }}
                onClick={() => {
                  setSearchTerm('');
                  setFilteredAuthorizations(authorizations);
                }}
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {patientId && user?.role === 'provider' && (
          <div className="action-buttons">
            <Link to={`/raise-pa/${patientId}`} className="btn btn-primary" style={{
              background: 'linear-gradient(45deg, #4299e1, #3182ce)',
              padding: '10px 20px',
              borderRadius: '30px',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(66, 153, 225, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              New Authorization
            </Link>
          </div>
        )}

        <div className="back-btn">
          {patientId ? (
            <Link to={`/patients/${patientId}`} className="btn" style={{
              background: 'white',
              padding: '8px 16px',
              borderRadius: '30px',
              color: '#4a5568',
              fontWeight: '600',
              fontSize: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 3px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              marginLeft: '10px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Patient
            </Link>
          ) : (
            <Link to="/dashboard">
            </Link>
          )}
        </div>
      </div>
      {selectedLetterId && <Modal authId={selectedLetterId} onClose={() => setSelectedLetterId(null)} />}
    </Layout>
  );
};

export default PriorAuthorizationList;
